import { TableCell } from "@/components/ui/table";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Cell, flexRender } from "@tanstack/react-table";
import { CSSProperties, memo, useMemo } from "react";

interface TableTdProps<T, TValue> {
  cell: Cell<T, TValue>;
}
const TableTdBase = <T, TValue>({ cell }: TableTdProps<T, TValue>) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const isPinned = cell.column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === "left" && cell.column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && cell.column.getIsFirstColumn("right");

  const style: CSSProperties = useMemo(
    () => ({
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px gray inset"
        : isFirstRightPinnedColumn
          ? "4px 0 4px -4px gray inset"
          : undefined,
      left: isPinned === "left" ? `${cell.column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${cell.column.getAfter("right")}px` : undefined,
      opacity: isDragging || isPinned ? 0.9 : 1,
      position: isPinned ? "sticky" : "relative",
      transform: CSS.Translate.toString(transform),
      width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
      transition: "width 0.2s linear, transform 0.2s linear",
      zIndex: isDragging || isPinned ? 1 : 0,
      overflow: "hidden",
    }),
    [isPinned, isDragging, transform, cell.column, isLastLeftPinnedColumn, isFirstRightPinnedColumn]
  );
  return (
    <TableCell
      style={style}
      ref={setNodeRef}
      className={`text-center min-w-0 w-full transition-transform  ${isPinned && "bg-background"} `}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
};
export const TableTd = memo(TableTdBase) as typeof TableTdBase;
